import { type SelectProps } from 'antd';
import { useMemo } from 'react';
import { type VariantConfiguratorListItemFragment } from '../../api/fragments';
import { type VehicleOptionsDataFragment } from '../../api/fragments/VehicleOptionsData';
import { useGetVehicleOptionsQuery } from '../../api/queries/getVehicleOptions';
import { type VehicleFilteringRule, type VehicleKind } from '../../api/types';
import FormFields from '../../themes/admin/Fields/FormFields';
import { useCompany } from '../contexts/CompanyContextManager';
import { type SelectFieldProps } from './SelectField';

export type VehicleSelectFieldProps = Omit<SelectFieldProps, 'children'> & {
    vehicles?: VehicleOptionsDataFragment[];
    vehicleKind?: VehicleKind;
    parentModelId?: string;
    // Vehicle module id
    moduleId?: string;
    dropdownOptions?: VariantConfiguratorListItemFragment[];
    // ModelIds
    modelIds?: string[];
    // Whether use suiteId or id as value
    useSuiteId?: boolean;
    autoSelected?: boolean;
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.label.toString().toLowerCase().includes(inputValue.toLowerCase());

const VehicleSelectField = ({
    vehicles: vehiclesFromProps,
    vehicleKind,
    parentModelId,
    modelIds,
    moduleId,
    dropdownOptions,
    useSuiteId,
    loading: loadingFromProps,
    autoSelected,
    ...props
}: VehicleSelectFieldProps) => {
    const company = useCompany(true);

    const filter = useMemo(
        (): VehicleFilteringRule => ({ vehicleKind, parentModelId, moduleId, modelIds }),
        [vehicleKind, parentModelId, moduleId, modelIds]
    );

    const { data, loading } = useGetVehicleOptionsQuery({
        fetchPolicy: 'cache-and-network',
        skip: dropdownOptions?.length > 0,
        variables: { filter: { ...filter, companyId: company?.id } },
    });

    const vehicles = useMemo(
        () => vehiclesFromProps || data?.vehicles?.items,
        [data?.vehicles?.items, vehiclesFromProps]
    );
    const options = useMemo(
        () =>
            dropdownOptions
                ? (dropdownOptions || []).map(option => ({
                      value: option?.variantId,
                      label: option?.displayName,
                  }))
                : (vehicles || []).map(vehicle => ({
                      value: useSuiteId ? vehicle.versioning.suiteId : vehicle.id,
                      label: vehicle.name.defaultValue,
                  })),
        [dropdownOptions, useSuiteId, vehicles]
    );

    return (
        <FormFields.SelectField
            {...props}
            autoSelected={autoSelected}
            filterOption={filterOption}
            loading={(!vehicles && loading) || loadingFromProps}
            options={options}
            showSearch
        />
    );
};

export default VehicleSelectField;
